import {useEffect, useRef, useState} from 'react'
import dayjs from 'dayjs'
import {useDialogContext, useDialogStore} from '@ariakit/react'
import {AxiosError} from 'axios'
import {
    Dialog,
    DialogDisclosure,
    DialogDismiss,
    DialogHeader,
    DialogHeading,
    DialogProvider
} from '@/components/ui/dialog-atoms/DialogAtoms'
import {Button} from '@/components/ui/button/Button'
import {Download01Icon} from '@/components/ui/icon/Icon'
import {useTranslation} from '@/translations/i18n'
import {usePermissions} from '@/features/auth/store/store'
import {ExportRiaError} from '@/features/accesses/types'
import {resolveExportRiaErrorLabel} from '@/features/accesses/utils'
import {useExportRia} from '@/features/accesses/queries/useExportRia'
import {flyToExportsToggle} from '@/components/commons/export-modal/flyToExports'
import {DateRangeFilterEnum} from '@/types'
import {ExportJob} from '@/features/exports/types'
import {useExportsIndicatorStore} from '@/features/exports/hooks/useExportsIndicator'
import {useExportAnnouncementStore} from '@/features/exports/hooks/useExportAnnouncement'
import {ExportRiaConfigStep, ExportRiaFormat, ExportRiaPeriod} from './ExportRiaConfigStep'

export const ExportRiaDialog = () => {
    const {t} = useTranslation()
    const permissions = usePermissions()
    const [isOpen, setIsOpen] = useState(false)
    const dialogStore = useDialogStore({open: isOpen, setOpen: setIsOpen})

    if (!permissions?.accesses?.write) {
        return null
    }

    return (
        <DialogProvider store={dialogStore}>
            <DialogDisclosure
                render={
                    <Button variant="primaryNeutral" size="sm" onClick={() => setIsOpen(true)}>
                        <Download01Icon />
                        {t('accesses:extract_ria')}
                    </Button>
                }
            />
            {isOpen && <ExportRiaDialogShell onClose={() => dialogStore.setOpen(false)} />}
        </DialogProvider>
    )
}

const ExportRiaDialogShell = ({onClose}: {onClose: () => void}) => {
    const {t} = useTranslation()
    const dialog = useDialogContext()
    const [period, setPeriod] = useState<ExportRiaPeriod | null>(null)
    const [format, setFormat] = useState<ExportRiaFormat>('csv')
    const [inlineError, setInlineError] = useState<string | null>(null)
    const hasFlownRef = useRef(false)
    const enqueuedJobRef = useRef<ExportJob | null>(null)

    const mutation = useExportRia({
        onSuccess: job => {
            useExportsIndicatorStore.getState().track(job.id, job.status)
            enqueuedJobRef.current = job
        },
        onError: (error: AxiosError<ExportRiaError>) => {
            setInlineError(t(resolveExportRiaErrorLabel(error.response?.data?.code)))
        }
    })

    // On a successful enqueue, "crumple" the dialog and fly it into the header exports button,
    // then close it — mirroring ActivityReportDialogShell's async-export handoff. With reduced
    // motion, a missing anchor, or no enqueued job we just close the dialog directly.
    useEffect(() => {
        if (!mutation.isSuccess || hasFlownRef.current) return
        hasFlownRef.current = true
        if (!enqueuedJobRef.current) {
            onClose()
            return
        }
        const dialogElement = dialog?.getState().contentElement ?? null
        void flyToExportsToggle(dialogElement).then(() => {
            const pendingJob = enqueuedJobRef.current
            const announced = useExportAnnouncementStore.getState().job
            if (pendingJob && announced?.id !== pendingJob.id) {
                useExportAnnouncementStore.getState().announce(pendingJob)
            }
            onClose()
        })
    }, [mutation.isSuccess, dialog, onClose])

    const handlePeriodApply = (nextPeriod: ExportRiaPeriod) => {
        setPeriod(nextPeriod)
        setInlineError(null)
    }

    const handleFormatChange = (nextFormat: ExportRiaFormat) => {
        setFormat(nextFormat)
        setInlineError(null)
    }

    const handleSubmit = () => {
        if (!period) return
        if (format === 'pdf' && period.range !== DateRangeFilterEnum.Enum.year) return
        const year = dayjs(period.startDate).year()
        const payload =
            period.range === DateRangeFilterEnum.Enum.year
                ? {year, format}
                : {year, month: dayjs(period.startDate).month() + 1, format}
        mutation.mutate(payload)
    }

    return (
        <Dialog width="560px">
            <DialogHeader>
                <DialogHeading>{t('accesses:export_ria_dialog_title')}</DialogHeading>
                <DialogDismiss />
            </DialogHeader>
            <ExportRiaConfigStep
                period={period}
                format={format}
                onPeriodApply={handlePeriodApply}
                onFormatChange={handleFormatChange}
                isSubmitting={mutation.isPending}
                submitError={inlineError}
                onSubmit={handleSubmit}
            />
        </Dialog>
    )
}
